import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'react-toastify';
import WindowForm from '../components/WindowForm';

interface WindowData {
  _id: string;
  name?: string;
  windowType: 'monthly' | 'weekly' | 'daily';
  startDayOfMonth?: number;
  startDayOfWeek?: number;
  startTime: string;
  endTime: string;
  intervalMinutes?: number;
  repeatDaysOfWeek?: number[];
  specificDate?: string;
  isRepeating: boolean;
  repeatStartDate?: string;
  repeatEndDate?: string;
  repeatExceptions: Date[];
  pricePerInterval: number;
  quantity: number;
  listingId: string;
}

const EditWindowPage: React.FC = () => {
  const { listingId, windowId } = useParams<{ listingId: string; windowId: string }>();
  const [window, setWindow] = useState<WindowData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchWindow = useCallback(async () => {
    if (!windowId) return;

    try {
      const { window: windowData } = await api.get<{ window: WindowData }>(
        `/windows/${windowId}`
      );
      setWindow(windowData);
    } catch {
      toast.error('Failed to load window');
    } finally {
      setIsLoading(false);
    }
  }, [windowId]);

  useEffect(() => {
    fetchWindow();
  }, [fetchWindow]);

  if (!listingId || !windowId) {
    return <div className="text-center py-8">Invalid window or listing ID</div>;
  }

  if (isLoading) {
    return <div className="text-center py-8">Loading window...</div>;
  }

  if (!window) {
    return <div className="text-center py-8">Window not found</div>;
  }

  // Prepare initial data for the form
  const initialData = {
    name: window.name || '',
    windowType: window.windowType,
    startDayOfMonth: window.startDayOfMonth?.toString() || '1',
    startDayOfWeek: window.startDayOfWeek?.toString() || '0',
    startTime: window.startTime || '09:00',
    endTime: window.endTime || '17:00',
    intervalMinutes: window.intervalMinutes?.toString() || '60',
    repeatDaysOfWeek: window.repeatDaysOfWeek || [],
    specificDate: window.specificDate
      ? new Date(window.specificDate).toISOString().split('T')[0]
      : '',
    isRepeating: window.isRepeating,
    repeatStartDate: window.repeatStartDate
      ? new Date(window.repeatStartDate).toISOString().split('T')[0]
      : '',
    repeatEndDate: window.repeatEndDate
      ? new Date(window.repeatEndDate).toISOString().split('T')[0]
      : '',
    repeatExceptions: window.repeatExceptions || [],
    pricePerInterval: window.pricePerInterval.toString(),
    quantity: window.quantity.toString(),
  };

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Edit Rental Window</h1>
      <WindowForm mode="edit" listingId={listingId} windowId={windowId} initialData={initialData} />
    </div>
  );
};

export default EditWindowPage;

